import { Link, createFileRoute } from "@tanstack/react-router";

import { CihClosingCta } from "~/components/cih/closing-cta";
import { CihLogoTile } from "~/components/cih/logo-tile";
import { CihSectionHeader } from "~/components/cih/section-header";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { Button } from "~/components/ui/button";
import {
  CCO_TYPE_DISPLAY_NAME,
  CCO_TYPE_ORDER,
  getGroupedCCOs,
  UNCATEGORIZED_KEY,
  type CcoDirectory,
} from "~/lib/ccos";
import { BRAND_URL } from "~/lib/shared/brand";
import { ccoMetaLabel } from "~/lib/shared/cco-card-meta";
import {
  createBrandMeta,
  createBreadcrumbStructuredData,
} from "~/lib/shared/meta";

const CANONICAL_URL = `${BRAND_URL}/organizations`;

export const Route = createFileRoute("/_public/organizations/")({
  staticData: { title: "Organizations" },
  loader: (): Promise<CcoDirectory> => getGroupedCCOs(),
  head: () => ({
    ...createBrandMeta({
      title: "Coaching Organizations | Dream Coach Match",
      description:
        "Find coaches certified by leading accreditation bodies and training organizations — ICF, EMCC, and the institutions that set coaching standards.",
      ogUrl: CANONICAL_URL,
      canonical: CANONICAL_URL,
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          createBreadcrumbStructuredData([
            { name: "Home", url: BRAND_URL },
            { name: "Organizations", url: CANONICAL_URL },
          ]),
        ),
      },
    ],
  }),
  component: OrganizationsIndex,
});

function OrganizationsIndex() {
  const { organizations, directoryMinCoaches } = Route.useLoaderData();

  return (
    <>
      <section className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 pt-6 pb-14 md:pt-8 md:pb-20 xl:px-0">
          <Breadcrumb
            items={[{ label: "Home", href: "/" }, { label: "Organizations" }]}
            isBold={false}
          />
          <div className="mt-6 text-xs font-medium tracking-[0.22em] text-purple-700 uppercase">
            ● Coaching Intelligence
          </div>
          <h1 className="mt-4 font-heading text-4xl font-medium tracking-tight text-gray-900 md:text-5xl lg:text-6xl xl:text-7xl">
            Coaching organizations
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-normal text-gray-700">
            The accrediting bodies and training organizations that define the
            coaching profession — and the coaches certified by them.
          </p>
        </div>
      </section>

      {CCO_TYPE_ORDER.map((type) => {
        const group = organizations[type];
        if (!group) return null;
        const subEntries = Object.entries(group);
        return (
          <section
            key={type}
            className="border-b border-gray-200 pb-14 md:pb-20"
          >
            <CihSectionHeader
              label="Coaching Intelligence"
              title={CCO_TYPE_DISPLAY_NAME[type]}
            />
            {subEntries.map(([sub, items], subIndex) => (
              <div key={sub}>
                {sub !== UNCATEGORIZED_KEY ? (
                  <div className="mx-auto max-w-7xl px-4 pt-8 pb-3 xl:px-0">
                    <div className="text-xs font-medium tracking-[0.22em] text-gray-400 uppercase">
                      {sub}
                    </div>
                  </div>
                ) : null}
                <div
                  className={`mx-auto max-w-7xl px-4 xl:px-0 ${
                    subIndex === subEntries.length - 1 ? "" : "mb-8"
                  }`}
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3">
                    {items.map((c) => (
                      <Link
                        key={c.id}
                        to="/organizations/$slug"
                        params={{ slug: c.slug ?? "" }}
                        className="group flex items-start gap-4 rounded-2xl border border-gray-100 bg-white p-6 shadow-xs transition duration-300 hover:-translate-y-0.5 hover:border-gray-200 hover:shadow-md md:p-8"
                      >
                        <CihLogoTile
                          name={c.name}
                          logoUrl={c.logoUrl}
                          className="h-14 w-14 shrink-0 md:h-16 md:w-16"
                        />
                        <div className="min-w-0 flex-1">
                          <h3 className="font-heading text-lg font-medium tracking-tight text-gray-900 md:text-xl">
                            {c.name}
                          </h3>
                          {c.description ? (
                            <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-gray-500">
                              {c.description}
                            </p>
                          ) : null}
                          <div className="mt-3 text-xs font-medium tracking-wider text-gray-500 uppercase">
                            {ccoMetaLabel(c, directoryMinCoaches) ??
                              "View organization"}{" "}
                            <span className="inline-block text-gray-400 transition duration-300 group-hover:translate-x-0.5 group-hover:text-gray-900">
                              →
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </section>
        );
      })}

      <CihClosingCta
        headline="Find a coach certified by any of these organizations."
        cta={
          <Link to="/discovery">
            <Button variant="primary" size="lg">
              Start →
            </Button>
          </Link>
        }
      />
    </>
  );
}
